import { createEffect, createSignal, onCleanup } from 'solid-js'
import { api, setCurrentProjectId, type ProjectSummary } from './api/client.ts'
import { renderThumbnail } from './export.ts'
import { resolveLocalAssets } from './localAssets.ts'
import { isCommitOpen, isDirty, loadScene, markClean, markDirty, snapshotScene } from './scene/store.ts'
import { emptyScene, type Scene } from './scene/types.ts'

export type SaveState = 'clean' | 'dirty' | 'saving' | 'error'

const AUTOSAVE_DELAY_MS = 1500

const [project, setProject] = createSignal<ProjectSummary | null>(null)
const [saveState, setSaveState] = createSignal<SaveState>('clean')
export { project, saveState }

function adopt(summary: ProjectSummary, scene: Scene): void {
  setProject(summary)
  setCurrentProjectId(summary.id)
  loadScene(scene)
  setSaveState('clean')
}

export async function createProject(name = 'Untitled'): Promise<void> {
  await flushCurrent()
  const summary = await api.createProject(name)
  adopt(summary, emptyScene())
}

export async function openProject(id: string): Promise<void> {
  await flushCurrent()
  const { summary, scene } = await api.loadProject(id)
  adopt(summary, scene)
}

export async function renameProject(name: string): Promise<void> {
  const current = project()
  if (!current) return
  await api.renameProject(current.id, name)
  setProject({ ...current, name })
}

/** The project id, creating a project first if nothing is open yet. */
let creating: Promise<string> | null = null

export async function ensureProject(): Promise<string> {
  const current = project()
  if (current) return current.id
  // Several images pasted at once each ask; they must share one project.
  creating ??= api
    .createProject('Untitled')
    .then((summary) => {
      setProject(summary)
      setCurrentProjectId(summary.id)
      return summary.id
    })
    .finally(() => {
      creating = null
    })
  return creating
}

export async function saveNow(): Promise<void> {
  const current = project()
  if (!current) return

  // Saving a local id would point the file at an asset that was never stored;
  // the upload calls back in here once it lands.
  const scene = resolveLocalAssets(snapshotScene())
  if (!scene) return
  // Cleared before the request, so edits made while it is in flight re-dirty
  // the document instead of being silently dropped.
  markClean()
  setSaveState('saving')
  try {
    await api.saveScene(current.id, scene)
    setSaveState('clean')
  } catch (err) {
    console.error('Autosave failed', err)
    markDirty()
    setSaveState('error')
  }
}

/** Write the scene and refresh the thumbnail. */
export async function flushCurrent(): Promise<void> {
  const current = project()
  if (!current) return
  await saveNow()
  try {
    const thumb = await renderThumbnail()
    if (thumb) await api.saveThumbnail(current.id, thumb)
  } catch (err) {
    console.warn('Thumbnail render failed', err)
  }
}

export async function deleteProject(id: string): Promise<void> {
  await api.deleteProject(id)
  if (project()?.id === id) {
    setProject(null)
    setCurrentProjectId(null)
    loadScene(emptyScene())
  }
}

export async function importProjectFile(file: File): Promise<void> {
  const summary = await api.importProject(file)
  await openProject(summary.id)
}

/**
 * Debounced autosave. `isDirty` only flips on the first edit of a burst, so the
 * single pending timer covers every edit that follows it.
 */
export function installAutosave(): void {
  createEffect(() => {
    if (!isDirty()) return
    if (!project()) return
    setSaveState('dirty')
    // Never mid-gesture: a save during a drag stalls the frames it lands on.
    let timer = 0
    const tick = () => {
      if (isCommitOpen()) timer = window.setTimeout(tick, AUTOSAVE_DELAY_MS)
      else void saveNow()
    }
    timer = window.setTimeout(tick, AUTOSAVE_DELAY_MS)
    onCleanup(() => clearTimeout(timer))
  })

  const onHide = () => {
    if (!project()) return
    // Fire and forget: the tab may be gone before this resolves, which is why
    // the autosave window is short.
    void flushCurrent()
  }

  const onBeforeUnload = (e: BeforeUnloadEvent) => {
    if (isDirty()) e.preventDefault()
  }

  window.addEventListener('pagehide', onHide)
  window.addEventListener('beforeunload', onBeforeUnload)

  onCleanup(() => {
    window.removeEventListener('pagehide', onHide)
    window.removeEventListener('beforeunload', onBeforeUnload)
  })
}

/** Open the most recent project, or start a fresh one if there are none. */
export async function openInitialProject(): Promise<void> {
  // Projects exist only in this browser; without this, storage pressure may
  // evict them. Best effort: browsers decide, and some never prompt.
  void navigator.storage?.persist?.()
  const all = await api.listProjects()
  const first = all[0]
  if (first) await openProject(first.id)
  else await createProject()
}
